/**
 * API基础配置 - 集成数据同步功能
 */
import axios from 'axios';
import { syncService } from './syncService';
import { useAuthStore } from '../store/authStore';

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof navigator !== 'undefined';
const getAuthStorageRaw = (): string | null => (isBrowser() ? localStorage.getItem('auth-storage') : null);
const isOnline = (): boolean => (isBrowser() ? navigator.onLine : true);

const redirectToLogin = () => {
  if (!isBrowser()) return;
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

let isSyncingPermissions = false;
let isFetchPermissionSyncInstalled = false;

const syncPermissionsIfNeeded = async (response: any) => {
  if (!isBrowser()) return;

  const headerValue = response?.headers?.['x-permissions-updated-at']
    || response?.headers?.get?.('x-permissions-updated-at');
  if (!headerValue) return;

  const serverPermissionsUpdatedAt = new Date(headerValue).getTime();
  if (Number.isNaN(serverPermissionsUpdatedAt)) return;

  const authStore = useAuthStore.getState();
  if (!authStore.isAuthenticated || !authStore.user || !authStore.token) return;

  const localPermissionsUpdatedAt = authStore.user.permissionsUpdatedAt || 0;
  if (serverPermissionsUpdatedAt <= localPermissionsUpdatedAt) return;
  if (isSyncingPermissions) return;

  isSyncingPermissions = true;
  try {
    console.log('[API] 检测到权限已更新，开始同步当前用户权限');
    await authStore.refreshUserPermissions();
  } finally {
    isSyncingPermissions = false;
  }
};

export const installFetchPermissionSync = () => {
  if (!isBrowser() || isFetchPermissionSyncInstalled || typeof window.fetch !== 'function') {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      await syncPermissionsIfNeeded(response);
    } catch (error) {
      console.error('[API] 同步请求权限失败:', error);
    }

    return response;
  };

  isFetchPermissionSyncInstalled = true;
};

// 创建axios实例
// 在生产环境中，如果没有设置环境变量，使用相对路径 /api
// In production, if no environment variable is set, use relative path /api
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 60000, // 增加到 60 秒，支持豆包 API 的 6-13 秒响应时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证token
// Request interceptor - add authentication token
api.interceptors.request.use(
  (config) => {
    // 从localStorage获取认证状态（与authStore的persist配置一致）
    // Get auth state from localStorage (consistent with authStore persist config)
    try {
      const authStorage = getAuthStorageRaw();
      if (authStorage) {
        const authState = JSON.parse(authStorage);
        // 尝试多种可能的路径获取 token
        const token = authState?.state?.token || authState?.token;

        if (token && token !== 'null' && token !== null) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('[API] Token 无效:', token);
        }
      } else {
        console.warn('[API] localStorage 中没有 auth-storage');
      }
    } catch (error) {
      console.error('[API] 获取认证token失败:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理认证错误
// Response interceptor - handle authentication errors
let isRefreshing = false; // 防止多个请求同时刷新 token

api.interceptors.response.use(
  async (response) => {
    await syncPermissionsIfNeeded(response);

    // 后端返回的格式是 { success: boolean, data: any, message?: string }
    // 直接返回完整的响应数据，保持后端的响应格式
    return response.data;
  },
  async (error) => {
    await syncPermissionsIfNeeded(error.response);

    // 统一错误处理
    const message = error.response?.data?.message || error.message || '请求失败';
    console.error('[API] 请求错误:', message);

    // 处理401未授权错误
    if (error.response?.status === 401) {
      console.warn('[API] 收到 401 响应');

      // 检查 localStorage 中是否有 token
      const authStorage = getAuthStorageRaw();
      if (authStorage) {
        const authState = JSON.parse(authStorage);
        const token = authState?.state?.token || authState?.token;

        console.log('[API] localStorage 中的 token:', token ? `${token.substring(0, 20)}...` : 'null');

        if (!token || token === 'null') {
          console.error('[API] Token 无效，跳转登录页');
          if (isBrowser()) {
            localStorage.removeItem('auth-storage');
          }
          redirectToLogin();
          return Promise.reject(error);
        }
      } else {
        console.error('[API] localStorage 中没有认证信息，跳转登录页');
        redirectToLogin();
        return Promise.reject(error);
      }

      // 如果已经在刷新，等待刷新完成
      if (isRefreshing) {
        console.log('[API] 正在刷新 token，等待...');
        return Promise.reject(error);
      }

      // 标记正在刷新
      isRefreshing = true;
      console.log('[API] Token 可能过期，尝试刷新...');

      // 尝试刷新 token（这里简化处理，直接跳转登录）
      // 如果需要实现 token 刷新，可以调用 refresh API
      setTimeout(() => {
        console.error('[API] Token 已失效，清除认证信息并跳转登录页');
        if (isBrowser()) {
          localStorage.removeItem('auth-storage');
        }
        redirectToLogin();
        isRefreshing = false;
      }, 100);

      return Promise.reject(error);
    }

    // 如果是网络错误且有缓存数据，提示用户
    if (!isOnline() && syncService.getPendingCount() > 0) {
      console.log('[API] 网络离线，数据已缓存');
    }

    return Promise.reject(error);
  }
);

// 增强的API方法，支持离线缓存
export const enhancedApi = {
  // 原始API实例
  raw: api,

  // 支持离线缓存的POST请求
  async post(url: string, data: any, options?: { cache?: boolean }) {
    const shouldCache = options?.cache !== false;

    try {
      const response = await api.post(url, data);
      return response;
    } catch (error) {
      // 如果网络错误且允许缓存，则缓存数据
      if (!isOnline() && shouldCache) {
        const type = this.getDataTypeFromUrl(url);
        if (type) {
          syncService.cacheData({
            type,
            action: 'create',
            data
          });
          console.log(`数据已缓存，将在网络恢复后同步: ${type}`);
          return { success: true, cached: true };
        }
      }
      throw error;
    }
  },

  // 支持离线缓存的PUT请求
  async put(url: string, data: any, options?: { cache?: boolean }) {
    const shouldCache = options?.cache !== false;

    try {
      const response = await api.put(url, data);
      return response;
    } catch (error) {
      // 如果网络错误且允许缓存，则缓存数据
      if (!isOnline() && shouldCache) {
        const type = this.getDataTypeFromUrl(url);
        if (type) {
          syncService.cacheData({
            type,
            action: 'update',
            data
          });
          console.log(`数据已缓存，将在网络恢复后同步: ${type}`);
          return { success: true, cached: true };
        }
      }
      throw error;
    }
  },

  // 支持离线缓存的DELETE请求
  async delete(url: string, options?: { cache?: boolean }) {
    const shouldCache = options?.cache !== false;

    try {
      const response = await api.delete(url);
      return response;
    } catch (error) {
      // 如果网络错误且允许缓存，则缓存数据
      if (!isOnline() && shouldCache) {
        const type = this.getDataTypeFromUrl(url);
        const id = this.getIdFromUrl(url);
        if (type && id) {
          syncService.cacheData({
            type,
            action: 'delete',
            data: { _id: id }
          });
          console.log(`删除操作已缓存，将在网络恢复后同步: ${type}`);
          return { success: true, cached: true };
        }
      }
      throw error;
    }
  },

  // GET请求（不缓存）
  async get(url: string, config?: any) {
    return api.get(url, config);
  },

  // 从URL获取数据类型
  getDataTypeFromUrl(url: string): 'worktime' | 'equipment' | 'material' | 'project' | 'priceconfig' | null {
    if (url.includes('/worktimes')) return 'worktime';
    if (url.includes('/equipment-usage')) return 'equipment';
    if (url.includes('/material-usage')) return 'material';
    if (url.includes('/projects')) return 'project';
    if (url.includes('/price-config')) return 'priceconfig';
    return null;
  },

  // 从URL获取ID
  getIdFromUrl(url: string): string | null {
    const matches = url.match(/\/([a-f\d]{24})$/);
    return matches ? matches[1] : null;
  }
};

const getTokenFromStorage = (): string | null => {
  try {
    const authStorage = getAuthStorageRaw();
    if (!authStorage) return null;
    const authState = JSON.parse(authStorage);
    return authState?.state?.token || authState?.token || null;
  } catch {
    return null;
  }
};

export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getTokenFromStorage();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return window.fetch(url, { ...options, headers });
};

export default api;
